// Conditions et effets : leur évaluation sur un état de partie. Aucune dépendance : repris par l'appli
// compagnon. Leur texte en français, pour la table des chemins du repli imprimé, est dans consignes.ts.

import type { Atome, Condition, Etat, Indice, Jauge, Suite } from './types.js';

/** Ce qu'il faut connaître de l'histoire pour évaluer ou écrire une condition. */
export interface Lexique {
  indices: Indice[];
  jauges: Jauge[];
}

export function estCombinaison(condition: Condition): condition is { tous: Atome[] } | { un: Atome[] } {
  return 'tous' in condition || 'un' in condition;
}

export function evaluerAtome(atome: Atome, etat: Etat): boolean {
  if ('a' in atome) return etat.indices.includes(atome.a);
  if ('pas' in atome) return !etat.indices.includes(atome.pas);
  const valeur = etat.jauges[atome.jauge] ?? 0;
  if (atome.min !== undefined && valeur < atome.min) return false;
  if (atome.max !== undefined && valeur > atome.max) return false;
  return true;
}

export function evaluer(condition: Condition, etat: Etat): boolean {
  if ('tous' in condition) return condition.tous.every((atome) => evaluerAtome(atome, etat));
  if ('un' in condition) return condition.un.some((atome) => evaluerAtome(atome, etat));
  return evaluerAtome(condition, etat);
}

/** La première suite dont la condition est vraie ; la dernière n'a pas de condition (le vérificateur l'exige). */
export function suiteRetenue(suites: Suite[], etat: Etat): Suite | undefined {
  return suites.find((suite) => suite.si === undefined || evaluer(suite.si, etat));
}

/** Applique les effets d'une suite et place la partie sur la scène d'arrivée. Ne modifie pas l'état reçu. */
export function appliquer(suite: Suite, etat: Etat, lexique: Lexique): Etat {
  const indices = new Set(etat.indices);
  for (const indice of suite.gagne ?? []) indices.add(indice);
  for (const indice of suite.perd ?? []) indices.delete(indice);
  const jauges: Record<string, number> = { ...etat.jauges };
  for (const [nom, ecart] of Object.entries(suite.jauges ?? {})) {
    const jauge = lexique.jauges.find((j) => j.id === nom);
    const maximum = jauge ? jauge.max : Number.POSITIVE_INFINITY;
    jauges[nom] = Math.min(maximum, Math.max(0, (jauges[nom] ?? 0) + ecart));
  }
  return { scene: suite.va, indices: [...indices].sort(), jauges };
}
